import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Card } from 'react-bootstrap-5';

import { connect } from 'react-redux';
import { addCriticalDate, setCriticalDate } from 'bundles/apex/deals/show/store/deal/actions';
import { createTask, loadTasks } from 'bundles/apex/deals/show/store/tasks/actions';
import { selectAll, clearSelection } from 'bundles/apex/deals/show/store/planner_bulk_actions/actions';

import AddCriticalDate from 'bundles/apex/deals/show/critical_dates/add';
import AddTask from 'bundles/apex/tasks/add';
import Button from 'shared/button';
import EmptyState from 'bundles/apex/planner/empty_state';
import Filters from './filters';
import Group from './group';
import LoadingSpinner from 'components/loading_spinner';
import PlannerTable from 'bundles/apex/planner/table';
import { getGroupedTasksAndDates } from 'bundles/apex/planner/helpers';
import { dateGroups } from './config';
import BulkEdit from './bulk_edit';

export const PlannerPanel = ({
  addCriticalDate,
  clearSelection,
  createTask,
  deal,
  editing,
  features,
  fields,
  forUser,
  hideBulkEdit,
  loadTasks,
  plannerFilters,
  selectAll,
  selection,
  setCriticalDate,
  tasks,
  users,
}) => {
  useEffect(() => {
    if (!tasks) {
      loadTasks();
    }
  }, [tasks]);

  let content;
  if (tasks) {
    const grouped = getGroupedTasksAndDates({ ...deal, tasks }, fields, plannerFilters);
    const [hasDates, hasTasks] = ['date', 'task'].map((type) => {
      return Object.values(grouped).some(group => group.some(item => item.type === type));
    });

    const selectionCount = Object.keys(selection).length;
    const taskCount = tasks.length || 0;

    const filters = (
      <div className="d-flex justify-content-between">
        <div>{!hideBulkEdit && hasTasks && <BulkEdit />}</div>
        <Filters />
      </div>
    );

    if (hasDates || hasTasks) {
      content = <>
        {!features.restrictedApex && filters}
        <PlannerTable
          hasDates={hasDates}
          hasTasks={hasTasks}
          selectAllChecked={selectionCount === taskCount && taskCount > 0}
          showIndeterminate={selectionCount !== taskCount && selectionCount > 0}
          showSelectColumn={editing}
          onSelectToggle={selectionCount === 0 ? selectAll : clearSelection}
        >
          {Object.keys(grouped).map((groupKey) => {
            return (
              <Group
                adminedOfficeIds={forUser.adminedOfficeIds}
                apexAdmin={forUser.apexAdmin}
                group={dateGroups[groupKey]}
                items={grouped[groupKey]}
                key={groupKey}
                requiredTasksEnabled={features.requiredTasks}
              />
            );
          })}
        </PlannerTable>
      </>;
    } else {
      content = <>
        {filters}
        <EmptyState message="Get started by adding a deal task or critical date">
          <AddTask
            asModal
            createTask={createTask}
            deal={deal}
            displayAs={Button.Primary}
            features={features}
            fields={fields}
            formDependenciesPath={deal.taskFormDependenciesPath}
            users={users}
          />
          <AddCriticalDate
            addCriticalDate={addCriticalDate}
            deal={deal}
            displayAs={Button.Primary}
            fields={fields}
            setCriticalDate={setCriticalDate}
          />
        </EmptyState>
      </>;
    }
  } else {
    content = <LoadingSpinner/>;
  }

  return (
    <Card className="planner">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <div className="fw-semibold">Planner</div>
          <div>
            {!features.restrictedApex && (
              <AddTask
                asModal
                className="pe-4"
                createTask={createTask}
                deal={deal}
                features={features}
                fields={fields}
                formDependenciesPath={deal.taskFormDependenciesPath}
                users={users}
              />
            )}
            <AddCriticalDate
              addCriticalDate={addCriticalDate}
              deal={deal}
              fields={fields}
              setCriticalDate={setCriticalDate}
            />
          </div>
        </div>
      </Card.Header>
      <Card.Body className="d-flex flex-column gap-3 overflow-y-auto">
        {content}
      </Card.Body>
    </Card>
  );
};

PlannerPanel.propTypes = {
  addCriticalDate: PropTypes.func,
  clearSelection: PropTypes.func,
  createTask: PropTypes.func,
  deal: PropTypes.object,
  editing: PropTypes.bool,
  features: PropTypes.shape({
    requiredTasks: PropTypes.bool,
    restrictedApex: PropTypes.bool,
  }).isRequired,
  fields: PropTypes.object,
  forUser: PropTypes.shape({
    adminedOfficeIds: PropTypes.arrayOf(PropTypes.number),
    apexAdmin: PropTypes.bool,
  }).isRequired,
  hideBulkEdit: PropTypes.bool,
  loadTasks: PropTypes.func.isRequired,
  plannerFilters: PropTypes.object.isRequired,
  selectAll: PropTypes.func,
  selection: PropTypes.object,
  setCriticalDate: PropTypes.func,
  tasks: PropTypes.array,
  users: PropTypes.array,
};

const mapStateToProps = state => ({
  deal: state.deal,
  editing: state.plannerBulkActions.editing,
  features: state.features,
  fields: state.fields,
  forUser: state.forUser,
  plannerFilters: state.plannerFilters,
  selection: state.plannerBulkActions.selection,
  tasks: state.tasks,
  users: state.options.users.sort((a, b) => a.label.localeCompare(b.label)),
});

const mapDispatchToProps = {
  addCriticalDate,
  clearSelection,
  createTask,
  loadTasks,
  selectAll,
  setCriticalDate,
};

export default connect(mapStateToProps, mapDispatchToProps)(PlannerPanel);
